<template>
  <h2 v-if="error.length" class="text-danger report-error-message">
    {{ error }}
  </h2>
  <div v-else-if="isLoading"><LoadingPage /></div>
  <div v-else id="slide-in-report" ref="slideInReport" data-test="cms-slide-in-report">
    <!-- pre strengths list section of report -->
    <div v-if="reportType.includes('viewpoints') && hasPermissionToCreateCustomReports">
      <div v-html="htmlBeforeViewpointsButtons"></div>
      <div class="container viewpoints-buttons">
        <button
          v-if="reportType === 'viewpoints' && htmlBeforeViewpointsButtons.length"
          type="button"
          class="btn btn-primary"
          @click="openInviteDialog"
          data-test="slide-in-report-invite-button"
        >
          Invite more people to review you
        </button>
        <CustomReportButton v-if="customReportData" v-bind="customReportData" class="btn-sm" />
        <ViewpointsInviteDialog v-if="reportType === 'viewpoints'" ref="viewpointsInviteDialog" />
      </div>
      <div v-html="htmlAfterViewpointsButtons" ref="htmlAfterViewpointsButtonsRef"></div>
    </div>
    <div v-else ref="preStrengthListRef" v-html="preStrengthListHtml"></div>
    <!-- end of pre strengths list section -->
    <section id="MappingStrengthsPage" class="team-theme" style="background: #f5f6f7">
      <div class="strengthListContainerContainer">
        <div class="strengthListContainer">
          <StrengthsListInner
            v-if="strengthListRatings"
            :ratings="strengthListRatings"
            :show-develop-button="false"
          />
          <div v-html="lessLikeYouHtml" class="lessLikeYou" style="background: white"></div>
        </div>
      </div>
    </section>
    <div v-html="postStrengthListHtml" ref="postStrengthListRef"></div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/quotes */
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import axios, { AxiosError } from "axios";
import StrengthsListInner from "../../components/strengths-lists/strengths-list-inner.vue";
import SessionInstanceState from "../../components/sessions/session-instance-state";
import { CmsDocument, CmsReportResource } from "../../services/cms/cms-type-definitions";
import { csContext } from "../../services/cs-context";
import {
  StrengthItemRatingApiModel,
  InviteControlApiModel,
  CustomReportApiModel,
} from "../../api-models";
import { CustomReportMemberTag } from "../../components/custom-report-api-model";
import onAccordionClick, {
  onDiamondButtonHover,
  onFitToProfileGraphClick,
  onLeaderTypeTitleClick,
} from "../../services/cms/cms-report-event-helper";
import ViewpointsInviteDialog from "../../components/invite-control/viewpoints-invite-dialog.vue";
import CustomReportButton from "../../components/custom-report-button.vue";
import LoadingPage from "../loading-page.vue";

type ComponentData = {
  ratings: StrengthItemRatingApiModel[];
};

type CustomReportButtonData = {
  id: string;
  kind: "Viewpoints" | "Team" | "TeamViewpoints";
  availableMemberTags: CustomReportMemberTag[];
  name: string;
  groups: CustomReportApiModel[];
  class: string;
};

const props = defineProps<{ doc: CmsDocument }>();
const doc = ref<CmsReportResource>(props.doc as CmsReportResource);

const isLoading = ref(true);
const error = ref("");
const preStrengthListHtml = ref("");
const strengthListRatings = ref<StrengthItemRatingApiModel[] | undefined>();
const postStrengthListHtml = ref("");
const lessLikeYouHtml = ref();

const preStrengthListRef = ref<HTMLDivElement>();
const slideInReport = ref<HTMLDivElement>();
const postStrengthListRef = ref<HTMLDivElement>();

const htmlAfterViewpointsButtonsRef = ref<HTMLDivElement>();

const viewpointsInviteDialog = ref<InstanceType<typeof ViewpointsInviteDialog>>();
const inviteModel = ref<InviteControlApiModel>();

const hasPermissionToCreateCustomReports = ref(true);

function normalizeString(input: string): string {
  return input.toLowerCase().replace(/\s+/g, "");
}

const reportType = computed(() => {
  return normalizeString(doc.value?.data.report_type);
});

function openInviteDialog() {
  if (inviteModel.value) {
    viewpointsInviteDialog.value?.open(inviteModel.value);
  }
}

function extractElement(html: string): string | null {
  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(html, "text/html");

  const elementToExtract = htmlDoc.querySelector(".lessLikeYou");
  lessLikeYouHtml.value = elementToExtract?.innerHTML;

  // remove from htmlstring as it will be rendered separately to help keep styling the same as the original report
  if (elementToExtract) {
    elementToExtract?.remove();
  }

  const serializer = new XMLSerializer();
  return serializer.serializeToString(htmlDoc);
}

const htmlBeforeViewpointsButtons = ref("");
const htmlAfterViewpointsButtons = ref("");
const customReportData = ref<CustomReportButtonData>();

// specific to the viewpoints reports - these buttons either don't render or
// lose functionality when rendering the report this way so we add them back in
// and parse the data from the v-bind attribute
function removeViewpointsButtons(htmlString: string) {
  const buttonsStartTag = '<p class="hidden-pdf viewpoints-report-buttons">';
  const buttonsEndTag = "</p>";
  // TODO: fix for when a user does not have permission to create custom reports - the section we're looking for won't be there
  // so it will not render any of the content before the strengths list section
  if (htmlString.includes(buttonsStartTag) && htmlString.includes(buttonsEndTag)) {
    const splitBefore = htmlString.split(buttonsStartTag);
    [htmlBeforeViewpointsButtons.value] = splitBefore;

    const splitAfter = splitBefore[1].split(buttonsEndTag);
    htmlAfterViewpointsButtons.value =
      splitAfter.length > 1 ? splitAfter.slice(1).join(buttonsEndTag) : "";

    const customReportButton = splitAfter[1];
    // extract props
    const propsRegex = /var\s+props\s*=\s*({[\s\S]*?});\s*/;
    const propsMatch = propsRegex.exec(customReportButton);

    if (!propsMatch || propsMatch.length < 2) {
      throw new Error("Props object not found within the section.");
    }

    // Extracted props object as a string
    const propsObjectString = propsMatch[1];
    if (propsObjectString) {
      // Clean up the props object string (remove \r\n)
      const cleanedPropsObjectString = propsObjectString.replace(/\r?\n|\r/g, "");
      const propsObject = JSON.parse(cleanedPropsObjectString);
      customReportData.value = { ...propsObject, class: "btn-sm" } as CustomReportButtonData;
    }
  } else {
    hasPermissionToCreateCustomReports.value = false;
  }
}

function extractPropsFromHtml(htmlString) {
  // This function exists because the strengths list doesn't render at all when rendering the whole html string
  // So we need to extract the props object from the html string and render the strengths list separately using the vue component

  // Regular expression to match the section with id="MappingStrengthsPage"
  const sectionRegex = /(<section\s+id="MappingStrengthsPage"[^>]*>[\s\S]*?<\/section>)/gi;

  const sectionMatch = sectionRegex.exec(htmlString);

  if (!sectionMatch) {
    throw new Error("Section with id='MappingStrengthsPage' not found.");
  }

  // Extracting the matched section content
  const sectionContent = sectionMatch[1];

  // Regular expression to extract props object from JavaScript code
  const propsRegex = /var\s+props\s*=\s*({[\s\S]*?});\s*/;
  const propsMatch = propsRegex.exec(sectionContent);

  if (!propsMatch || propsMatch.length < 2) {
    throw new Error("Props object not found within the section.");
  }

  // Extracted props object as a string
  const propsObjectString = propsMatch[1];

  const cleanedPropsObjectString = propsObjectString.replace(/\r?\n|\r/g, "");

  const propsObject = JSON.parse(cleanedPropsObjectString) as ComponentData;

  // Extract strings before and after the section
  const beforeStrengthsList = htmlString.substring(0, sectionMatch.index + sectionMatch[0].length);
  const beforeWithLessLikeYouExtracted = extractElement(beforeStrengthsList) || beforeStrengthsList;
  const afterStrengthsList = htmlString.substring(sectionMatch.index + sectionMatch[0].length);

  return {
    propsData: propsObject,
    beforeComponent: beforeWithLessLikeYouExtracted,
    afterComponent: afterStrengthsList,
  };
}

function splitHtml(htmlString: string) {
  // this is to handle the fact that strengthsListInner is a vue component which doesn't render when just
  // using v-html for the whole html string
  const { propsData, beforeComponent, afterComponent } = extractPropsFromHtml(htmlString);
  if (!propsData) {
    error.value = "Error parsing the report";
    return;
  }
  strengthListRatings.value = propsData.ratings;
  preStrengthListHtml.value = beforeComponent;
  postStrengthListHtml.value = afterComponent;

  if (reportType.value.includes("viewpoints")) {
    removeViewpointsButtons(preStrengthListHtml.value);
  }
}

async function fetchRazorView() {
  let API_URL = `/api/reporthtml/${reportType.value}`;

  const isATeamReport = reportType.value.includes("team");

  if (isATeamReport && !csContext.currentTeam) {
    error.value = "Error fetching the report: you are not in a team.";
    return;
  }

  if (isATeamReport && !!csContext.currentTeam) {
    API_URL = `${API_URL}/${csContext.currentTeam?.id}`;
  }

  try {
    const response = await axios.get(API_URL);
    splitHtml(response.data);
  } catch (err) {
    const e = err as AxiosError;
    error.value = `Error fetching the report: ${e.response?.data}`;
  } finally {
    isLoading.value = false;
  }
}

// add lost functionality back in (eg, clicking on the fit-to-profile results)
watch(preStrengthListRef, () => {
  if (!preStrengthListRef.value) {
    return;
  }
  preStrengthListRef.value.addEventListener("click", (event: MouseEvent) => {
    onFitToProfileGraphClick(event);
  });
  preStrengthListRef.value.addEventListener("mouseover", (event: MouseEvent) => {
    onLeaderTypeTitleClick(event);
  });
  preStrengthListRef.value.addEventListener("mouseover", (event: MouseEvent) => {
    onDiamondButtonHover(event);
  });
});

watch(postStrengthListRef, () => {
  if (postStrengthListRef.value) {
    postStrengthListRef.value.addEventListener("mouseover", (event: MouseEvent) => {
      onLeaderTypeTitleClick(event);
    });
  }
});

watch(htmlAfterViewpointsButtonsRef, () => {
  if (htmlAfterViewpointsButtonsRef.value) {
    htmlAfterViewpointsButtonsRef.value.addEventListener("click", (event: MouseEvent) => {
      onFitToProfileGraphClick(event);
    });
  }
});

onMounted(async () => {
  // Add some styles to the body which would normally be there when viewing a report.
  document.body.classList.add("maxWidth-sm");
  document.body.classList.add("text-big");
  await fetchRazorView();
  SessionInstanceState.set("reportIsOpen", true);

  if (reportType.value === "viewpoints") {
    const response = await axios.get("/InviteCodes/ViewpointsReviewInviteModel");
    inviteModel.value = response.data;
  }
});

onBeforeUnmount(() => {
  document.body.classList.remove("maxWidth-sm");
  document.body.classList.remove("text-big");
  preStrengthListRef.value?.removeEventListener("click", onAccordionClick);
  preStrengthListRef.value?.removeEventListener("mouseover", onLeaderTypeTitleClick);
  preStrengthListRef.value?.removeEventListener("mouseover", onDiamondButtonHover);
  postStrengthListRef.value?.removeEventListener("mouseover", onLeaderTypeTitleClick);
  if (htmlAfterViewpointsButtonsRef.value) {
    htmlAfterViewpointsButtonsRef.value.removeEventListener("click", onFitToProfileGraphClick);
  }
  SessionInstanceState.set("reportIsOpen", false);
});
</script>
<style scoped lang="less">
.report-error-message {
  margin: 20px;
  text-align: center;
}

.viewpoints-buttons {
  button {
    height: 46px;
  }
}
</style>
<style lang="less">
#slide-in-report {
  div.itemTag {
    font-size: 14px;
  }
}
</style>
